import * as Vue from "vue"
import { createPinia } from 'pinia'
import router from "controllers/vue_components/router"
import VCalendar from 'v-calendar'
import { useGlobalStore } from 'stores/global.js'
import { useNotificationsStore } from 'stores/notifications.js'
import { unlockAudio } from 'controllers/vue_components/notifications/notificationSounds.js'
import { HIGHCHARTS_THEME, highchartsLang } from 'utils/highchartsTheme.js'
import { prefetchRoute } from 'utils/prefetchRoute.js'
import { reloadForNewDeploy } from 'utils/chunkReload.js'
import { i18nPlugin } from 'composables/useI18n.js'

// Keep the AudioContext unlocked. Called on every click/keydown so that
// if the browser suspends it (e.g. after inactivity), the next interaction
// wakes it back up before any WebSocket-triggered sound tries to play.
document.addEventListener('click',   unlockAudio, true)
document.addEventListener('keydown', unlockAudio, true)
// Shell: rendered on every dashboard page by the layout, so it stays in the entry bundle.
import AppSidebar from "controllers/vue_components/AppSidebar"
import AppHellobar from "controllers/vue_components/hellobar/AppHellobar"
import AppShell from "controllers/vue_components/AppShell"
import FlashMessage from "controllers/vue_components/flash_message"
import BottomNav from "controllers/vue_components/BottomNav"

const point = "#calendar_app"

// Everything else loads on demand. These are the components a Rails view mounts with a
// <tag>, plus the ones other components use as global tags (copy-text, event, event-table,
// quill-editor). A page only downloads the ones it renders. Do not import any of them
// statically here: that would pull them, and everything they import, back into the entry.
const lazyComponents = {
  'onboarding': () => import("controllers/vue_components/onboarding"),
  'public-event': () => import("controllers/vue_components/public_event/index"),
  'home-navbar': () => import("controllers/vue_components/navbar"),
  'copy-text': () => import("controllers/vue_components/copy_text"),
  'event': () => import("controllers/vue_components/events/event"),
  'event-table': () => import("controllers/vue_components/events/index/table"),
  'bookings-list': () => import("controllers/vue_components/events/booking/list"),
  'cancel-booking': () => import("controllers/vue_components/events/booking/cancel"),
  'vue-confetti': () => import("controllers/vue_components/vue_confetti"),
  'cookies-modal': () => import("controllers/vue_components/cookies_modal"),
  'attendees-list': () => import("controllers/vue_components/events/booking/attendees/list"),
  'integrations-list': () => import("controllers/vue_components/integrations/list"),
  'integrate-external-payments': () => import("controllers/vue_components/integrations/external_payments"),
  'user-webhooks': () => import("controllers/vue_components/integrations/webhooks"),
  'org-index': () => import("controllers/vue_components/organizations"),
  'org-onboarding': () => import("controllers/vue_components/organizations/onboarding"),
  'public-message-form': () => import("controllers/vue_components/public/public_message_form"),
  'troubleshoot': () => import("controllers/vue_components/public/troubleshoot"),
  'pricing-index': () => import("controllers/vue_components/pricing"),
  'social-icons': () => import("controllers/vue_components/events/social_icon"),
  'admin-user-invitations': () => import("controllers/vue_components/admin/users/invitations"),
  'quill-editor': () => import("controllers/vue_components/fields/quill_editor"),
}

// Start a page's chunk when the pointer lands on a link to it (or a finger touches it).
// RouterLink renders a plain <a href>, so one delegated listener covers every link.
// Public pages have no router, so they are left alone.
function prefetchFromEvent(event) {
  const link = event.target.closest?.('a[href^="/"]')
  if (!link || document.querySelector('#calendar_app[data-public-page="true"]')) return
  prefetchRoute(link.getAttribute('href'))
}
document.addEventListener('mouseover', prefetchFromEvent, { passive: true })
document.addEventListener('touchstart', prefetchFromEvent, { passive: true })
document.addEventListener('focusin', prefetchFromEvent)

// The chunk of a page a Rails view mounts is requested from the HTML that arrived, so a
// deploy between that load and the request leaves a stale file name. Reload for the new build.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  reloadForNewDeploy()
})

function pctFromElapsed(ms) {
  if (ms < 1000) return Math.floor(ms / 1000 * 30)
  if (ms < 3000) return Math.floor(30 + (ms - 1000) / 2000 * 30)
  return Math.min(Math.floor(60 + (1 - Math.pow(1 - Math.min((ms - 3000) / 8000, 1), 2)) * 39), 99)
}

// Org loader panel only (app/views/layouts/organization.html.erb) — no-op
// elsewhere since #app-loader-status doesn't exist on other layouts.
function stageFromPct(v) {
  if (v < 30) return 'Authenticating workspace'
  if (v < 60) return 'Loading organization'
  if (v < 90) return 'Preparing modules'
  return 'Finalizing'
}

function startPctCounter() {
  if (window._pctRafId) return  // inline script already running — let it continue
  const el = document.getElementById('app-loader-pct')
  if (!el) return
  const statusEl = document.getElementById('app-loader-status')
  // User layout only — the org loader's bar is a CSS scan animation, not a fill.
  const fillEl = document.getElementById('app-loader-fill')
  el.textContent = '0%'
  if (fillEl) {
    // Reset without transitioning backwards from the previous 100%.
    fillEl.style.transition = 'none'
    fillEl.style.width = '0%'
    void fillEl.offsetWidth
    fillEl.style.transition = ''
  }
  const startTime = performance.now()
  function tick(now) {
    const v = pctFromElapsed(now - startTime)
    el.textContent = v + '%'
    if (fillEl) fillEl.style.width = v + '%'
    if (statusEl) statusEl.textContent = stageFromPct(v)
    if (v < 99) window._pctRafId = requestAnimationFrame(tick)
    else window._pctRafId = null
  }
  window._pctRafId = requestAnimationFrame(tick)
}

function finishPctCounter() {
  if (window._pctRafId) { cancelAnimationFrame(window._pctRafId); window._pctRafId = null }
  // Snap to 100% so the count completes instead of freezing mid-number as it fades.
  const fillEl = document.getElementById('app-loader-fill')
  if (fillEl) {
    fillEl.style.width = '100%'
    const el = document.getElementById('app-loader-pct')
    if (el) el.textContent = '100%'
  }
}

function startAppProgress(startPct = true) {
  const bar = document.getElementById('app-top-bar')
  if (bar) {
    bar.style.transition = 'none'
    bar.style.opacity = '1'
    bar.style.width = '0%'
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        bar.style.transition = 'width 0.4s ease-out'
        bar.style.width = '12%'
        setTimeout(() => {
          bar.style.transition = 'width 8s cubic-bezier(0.05, 0, 0, 1)'
          bar.style.width = '82%'
        }, 400)
      })
    })
  }
  if (startPct) startPctCounter()
}

function finishAppProgress() {
  finishPctCounter()
  const bar = document.getElementById('app-top-bar')
  if (!bar) return
  bar.style.transition = 'width 0.2s ease-out'
  bar.style.width = '100%'
  setTimeout(() => {
    bar.style.transition = 'opacity 0.35s ease'
    bar.style.opacity = '0'
    setTimeout(() => {
      bar.style.transition = 'none'
      bar.style.opacity = '1'
      bar.style.width = '0%'
    }, 350)
  }, 200)
}

// Inline script in the HTML body handles the pct counter for initial hard load.
// Only kick off the top bar here.
startAppProgress(false)

let vueApp = null;
const pinia = createPinia()

// Pinia calls `registerPiniaDevtools` → `setupDevtoolsPlugin` on every
// `app.use(pinia)`. With Turbo Drive a new Vue app is created on every
// navigation, so this fires repeatedly. The Vue DevTools browser-extension
// hook becomes stale after the first app unmounts, causing an uncaught
// "Cannot read properties of undefined (reading 'app')" promise rejection.
// Fix: allow devtools to register on the first install only. On subsequent
// installs, temporarily hide the global devtools hook so Pinia's
// `setupDevtoolsPlugin` call is a no-op, then restore it immediately.
const _piniaInstall = pinia.install.bind(pinia)
let _piniaDevtoolsDone = false
pinia.install = function (app) {
  if (_piniaDevtoolsDone) {
    // Vue DevTools may define __VUE_DEVTOOLS_GLOBAL_HOOK__ as a getter-only
    // property, making direct assignment throw. Use defineProperty to
    // temporarily shadow it; fall back gracefully if the descriptor is
    // non-configurable (e.g. certain browser extensions or locked environments).
    const savedHook = window.__VUE_DEVTOOLS_GLOBAL_HOOK__
    let suppressed = false

    try {
      Object.defineProperty(window, '__VUE_DEVTOOLS_GLOBAL_HOOK__', {
        configurable: true,
        get: () => undefined,
      })
      suppressed = true
    } catch (e) {
      console.warn('[pinia-devtools] Could not suppress __VUE_DEVTOOLS_GLOBAL_HOOK__ — proceeding without suppression. Devtools may emit a stale-app warning.', e)
    }

    try {
      _piniaInstall(app)
    } catch (e) {
      console.error('[pinia-devtools] Pinia install failed.', e)
    }

    if (suppressed) {
      try {
        Object.defineProperty(window, '__VUE_DEVTOOLS_GLOBAL_HOOK__', {
          configurable: true,
          get: () => savedHook,
        })
      } catch (e) {
        console.warn('[pinia-devtools] Could not restore __VUE_DEVTOOLS_GLOBAL_HOOK__.', e)
      }
    }
  } else {
    _piniaDevtoolsDone = true
    _piniaInstall(app)
  }
}

document.addEventListener("turbo:before-render", function () {
  if (vueApp !== null) {
    vueApp.unmount();
    vueApp = null;
  }
});

// After every Turbo page swap: re-show the overlay and restart the progress bar.
document.addEventListener("turbo:render", function () {
  const loader = document.getElementById('app-loader')
  if (loader) loader.classList.remove('app-loader-out')
  startAppProgress()
});

// Apply Highcharts theme globally — runs once, affects all chart() / mapChart() calls.
if (typeof Highcharts !== 'undefined') Highcharts.setOptions({ ...HIGHCHARTS_THEME, lang: highchartsLang() })

document.addEventListener("turbo:load", async function () {
      const element = document.querySelector(point)
      if (element !== null && vueApp === null && element.innerHTML.trim() !== '') {
        // A Rails view that mounts a lazy component would otherwise paint before its chunk
        // arrives. Fetch the ones present in the server HTML first, in parallel.
        await Promise.all(
          Object.entries(lazyComponents)
            .filter(([tag]) => element.querySelector(tag))
            .map(([, load]) => load().catch(() => {}))
        )
        // Another turbo:load mounted an app, or Turbo swapped the page, while we waited.
        if (vueApp !== null || !element.isConnected) return

        const app = Vue.createApp({
          data() {
            return {count: 1}
          },
        })

        // Declare Global Variables
        let globalVariablesDiv = document.getElementById("global-variables");
        let globalVariables = globalVariablesDiv.getAttribute("data");
        for (const [key, value] of Object.entries(JSON.parse(globalVariables))) {
          window[key] = value;
        }

        app.use(VCalendar, {})
        app.use(pinia)
        app.use(i18nPlugin)

        // Init global store with user + app data from Rails globals. This has to happen
        // before the router installs, because its first navigation runs route guards
        // (for example the integration check on /jobs) that read this store.
        const globalStore = useGlobalStore()
        globalStore.init()

        // Public pages (profile, event booking) use Turbo for navigation, not Vue Router.
        // Installing the router on these pages causes history.replaceState conflicts with
        // Turbo Drive, making the URL revert to the previous page after navigation.
        const isPublicPage = element.dataset.publicPage === 'true'

        if (!isPublicPage) {
          app.use(router)

          // Unlock AudioContext on every SPA navigation — router.beforeEach fires
          // synchronously inside the user's click, giving us the cleanest gesture window.
          router.beforeEach(() => { unlockAudio() })

          // Keep sidebar active-link state in sync on every SPA navigation
          router.afterEach((to) => {
            useGlobalStore().setCurrentPath(to.path)
          })
        }

        // Open ActionCable connection for the whole session (not just /notifications)
        if (globalStore.user.id) {
          useNotificationsStore().subscribeToChannel()
          globalStore.subscribeToUserChannel()
        }
        // Shell components are always available; the rest register as async wrappers.
        app.component('app-shell', AppShell)
        app.component('flash-message', FlashMessage)
        app.component('bottom-nav', BottomNav)
        app.component('app-sidebar', AppSidebar)
        app.component('app-hellobar', AppHellobar)
        for (const [tag, load] of Object.entries(lazyComponents)) {
          app.component(tag, Vue.defineAsyncComponent(load))
        }

        vueApp = app;
        app.mount(point)

        // The first route's chunk loads during router install. Keep the loading overlay up
        // until it has rendered, so it never fades out onto an empty page.
        const firstRouteReady = isPublicPage ? Promise.resolve() : router.isReady().catch(() => {})
        firstRouteReady.then(() => requestAnimationFrame(() => {
          const loader = document.getElementById('app-loader')
          if (loader) loader.classList.add('app-loader-out')
          finishAppProgress()
        }))
      }
    }
)
